import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsUpDown, LogOut, UserCog } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { SidebarMenuButton } from "~/components/ui/sidebar";
import { api } from "~/lib/api";
import { useMe } from "~/lib/auth";

function initials(name?: string | null, phone?: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  }
  return phone?.slice(-2) ?? "PP";
}

export function NavUser() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function signOut() {
    try { await api.logout(); } catch { /* ignore */ }
    qc.clear();
    await navigate({ to: "/login" });
  }

  const roleLabel = me?.role === "ops_admin" ? "Administrator" : "Agent";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
          <Avatar className="size-7 rounded-md">
            <AvatarFallback className="rounded-md text-[11px] font-medium uppercase">{initials(me?.name, me?.phone)}</AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left leading-tight">
            <span className="truncate text-[13px] font-medium">{me?.name || me?.phone || "Staff"}</span>
            <span className="text-text-faint truncate text-[11px]">{roleLabel}</span>
          </div>
          <ChevronsUpDown className="text-text-faint ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" sideOffset={8} className="min-w-[200px]">
        <DropdownMenuLabel className="font-normal">
          <div className="grid leading-tight">
            <span className="text-[13px] font-medium">{me?.name || "Staff"}</span>
            <span className="text-text-faint text-[11px]">{me?.phone}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: "/ops/account" })}>
          <UserCog className="size-3.5" /> Account
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut className="size-3.5" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
